import { computed, type ComputedRef } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { getRelatedArticles } from '@/common/api/api.pleased';
import type { SearchArticle } from '@/common/api/models.pleased';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';

/**
 * TanStack Query hook to fetch related articles
 * Provides automatic caching, loading states, and error handling
 *
 * @param articleId - Article ID to fetch related articles for
 * @returns TanStack Query result with related articles data
 *
 * @example
 * ```ts
 * const articleId = computed(() => Number(route.params.id));
 * const { data: relatedArticles } = useRelatedArticles(articleId);
 * ```
 */
export const useRelatedArticles = (articleId: ComputedRef<string | number | undefined>) => {
  const mockedFlowStore = useMockedFlowStore();
  const { isActive: isMockedFlowActive, relatedArticles: mockedRelatedArticles } = storeToRefs(mockedFlowStore);

  return useQuery({
    queryKey: ['relatedArticles', articleId],
    queryFn: async (): Promise<SearchArticle[]> => {
      if (isMockedFlowActive.value && mockedRelatedArticles.value) {
        return mockedRelatedArticles.value;
      }

      if (!articleId.value) {
        throw new Error('Article ID is required');
      }
      const response = await getRelatedArticles.cached({ articleId: articleId.value });
      return response.data;
    },
    enabled: computed(() => !!articleId.value),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    retry: true,
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 60_000),
  });
};
